import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/case-workspace-premium.css';
import CaseDetailsSegment from '../components/AddService/CaseDetailsSegment';
import CaseNotesSegment from '../components/AddService/CaseNotesSegment';
import CaseServicesSegment from '../components/AddService/CaseServicesSegment';
import ServiceDetailsSegment from '../components/AddService/ServiceDetailsSegment';
import CaseWorkspaceShell from '../components/AddService/CaseWorkspaceShell';
import CaseWorkspaceHero from '../components/AddService/CaseWorkspaceHero';
import CustomerDetailsModal from '../components/AddService/CustomerDetailsModal';
import VendorLiveTracking from '../components/VendorLiveTracking';
import { getCaseById, getServiceById } from '../services/addService.service';
import { runInLane } from '../utils/requestLane';
import { serviceStatusAllowsVendorTracking } from '../utils/vendorTracking';

const AddService = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get case_id and service_id from URL params or location state
  const case_id = searchParams.get('case_id') || location.state?.case_id;
  const service_id = searchParams.get('service_id') || location.state?.service_id;

  const [caseData, setCaseData] = useState(null);
  const [loadingCase, setLoadingCase] = useState(true);
  const [errorCase, setErrorCase] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [detailedServiceData, setDetailedServiceData] = useState(null);
  const [loadingDetailedService, setLoadingDetailedService] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [activityCounts, setActivityCounts] = useState({ notes: 0, files: 0 });
  const [photoCountKpi, setPhotoCountKpi] = useState(0);
  const [trackVendorModalOpen, setTrackVendorModalOpen] = useState(false);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [paymentRefreshKey, setPaymentRefreshKey] = useState(0);

  // Fetch detailed service information
  const fetchDetailedService = useCallback(async (serviceId) => {
    if (!serviceId) {
      setDetailedServiceData(null);
      return;
    }
    
    // Don't fetch if we're already loading
    if (loadingDetailedService) {
      return;
    }
    
    console.log('AddService: Fetching detailed service for ID:', serviceId);
    setLoadingDetailedService(true);
    try {
      const response = await getServiceById(serviceId);
      console.log('AddService: Detailed service data received:', response?.service?.service_number);
      setDetailedServiceData(response);
    } catch (error) {
      console.error('Failed to fetch detailed service:', error);
      setDetailedServiceData(null);
    } finally {
      setLoadingDetailedService(false);
    }
  }, [loadingDetailedService]);

  // Ensure case_id and service_id are in URL params
  useEffect(() => {
    if (case_id && !searchParams.get('case_id')) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('case_id', case_id.toString());
        return newParams;
      });
    }
    if (service_id && !searchParams.get('service_id')) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('service_id', service_id.toString());
        return newParams;
      });
    }
  }, [case_id, service_id, searchParams, setSearchParams]);

  // If we only have service_id (e.g. payment-failed toast), look up case_id so the workspace can load.
  useEffect(() => {
    if (case_id || !service_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getServiceById(service_id);
        const resolvedCaseId =
          response?.service?.case_id ?? response?.case_id ?? response?.case?.id;
        if (!cancelled && resolvedCaseId) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('case_id', String(resolvedCaseId));
            next.set('service_id', String(service_id));
            return next;
          });
        } else if (!cancelled) {
          setErrorCase('No Case ID provided.');
          setLoadingCase(false);
        }
      } catch {
        if (!cancelled) {
          setErrorCase('No Case ID provided.');
          setLoadingCase(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [case_id, service_id, setSearchParams]);

  // Fetch case details
  useEffect(() => {
    if (!case_id) {
      return;
    }

    const fetchCaseDetails = async () => {
      setLoadingCase(true);
      setErrorCase(null);
      try {
        const response = await getCaseById(case_id);
        setCaseData(response);
      } catch (error) {
        setErrorCase(error.message || 'Failed to fetch case details.');
        setCaseData(null);
      } finally {
        setLoadingCase(false);
      }
    };

    fetchCaseDetails();
  }, [case_id]);

  // Handle service_id changes separately
  useEffect(() => {
    if (service_id && caseData?.services && caseData.services.length > 0) {
      const targetService = caseData.services.find(service => 
        service.id === parseInt(service_id) || service.id === service_id
      );
      if (targetService && (!selectedService || selectedService.id !== targetService.id)) {
        console.log('AddService: Selecting service from URL/state:', targetService.service_number, 'ID:', targetService.id);
        setSelectedService(targetService);
        // Fetch detailed service data
        setLoadingDetailedService(true);
        runInLane(() => getServiceById(targetService.id))
          .then(detailedResponse => {
            console.log('AddService: Detailed service data loaded for:', targetService.service_number);
            setDetailedServiceData(detailedResponse);
          })
          .catch(detailedError => {
            console.error('Failed to fetch detailed service:', detailedError);
            setDetailedServiceData(null);
          })
          .finally(() => {
            setLoadingDetailedService(false);
          });
      } else if (service_id && caseData?.services && caseData.services.length > 0) {
        console.warn('AddService: Service with ID', service_id, 'not found in case services');
      }
    }
    // selectedService object intentionally omitted; id comparison is sufficient
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service_id, caseData?.services, selectedService?.id]);


  // Separate function to refresh only services list without affecting other segments
  const refreshServicesOnly = useCallback(async () => {
    if (!case_id) return;
    try {
      const response = await getCaseById(case_id);
      // Only update the services part of the case data, keep other segments unchanged
      setCaseData(prevData => ({
        ...prevData,
        services: response.services || []
      }));
      return response;
    } catch (error) {
      console.error('Failed to refresh services:', error);
      throw error;
    }
  }, [case_id]);

  // Handle service selection
  const handleServiceSelection = useCallback((service) => {
    setSelectedService(service);
    
    // Update URL with selected service_id
    if (service) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('service_id', service.id.toString());
        return newParams;
      });
      fetchDetailedService(service.id);
    } else {
      // Remove service_id from URL if no service selected
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('service_id');
        return newParams;
      });
      setDetailedServiceData(null);
    }
  }, [fetchDetailedService, setSearchParams]);

  // Handle when a new service is created
  const handleServiceCreated = useCallback(async (newServiceId) => {
    console.log('AddService: New service created with ID:', newServiceId);
    try {
      // Refresh services list to get the updated services
      const updatedCaseData = await refreshServicesOnly();
      console.log('AddService: Services refreshed, services count:', updatedCaseData?.services?.length);
      
      // After refreshing, find and select the new service
      if (updatedCaseData?.services && newServiceId) {
        const newService = updatedCaseData.services.find(service => service.id === newServiceId);
        if (newService) {
          console.log('AddService: Found new service, selecting it:', newService.service_number);
          setSelectedService(newService);
          
          // Update URL with new service_id
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('service_id', newServiceId.toString());
            return newParams;
          });
          
          fetchDetailedService(newServiceId);
        } else {
          console.warn('AddService: New service not found in refreshed data');
        }
      }
    } catch (error) {
      console.error('Error refreshing data after service creation:', error);
    }
  }, [fetchDetailedService, refreshServicesOnly, setSearchParams]);

  // Handle when services are added/updated
  const handleServiceAdded = useCallback(() => {
    refreshServicesOnly();
  }, [refreshServicesOnly]);

  // Handle when service types are loaded
  const handleServiceTypesLoaded = useCallback((types) => {
    setServiceTypes(types);
  }, []);

  // Memoize the onServiceUpdated callback to prevent unnecessary re-renders
  const handleServiceUpdated = useCallback(() => {
    refreshServicesOnly();
  }, [refreshServicesOnly]);

  const handlePaymentActivity = useCallback(() => {
    refreshServicesOnly();
    setNotesRefreshKey((key) => key + 1);
    setPaymentRefreshKey((key) => key + 1);
    if (selectedService?.id) {
      getServiceById(selectedService.id)
        .then((response) => setDetailedServiceData(response))
        .catch((error) => {
          console.error('Failed to refresh service after payment update:', error);
        });
    }
  }, [refreshServicesOnly, selectedService?.id]);

  // Background refresh function for service details
  const refreshServiceDetailsInBackground = useCallback(async () => {
    if (!selectedService?.id) return;
    
    try {
      console.log('AddService: Background refresh - fetching updated service details for:', selectedService.service_number);
      const response = await getServiceById(selectedService.id);
      setDetailedServiceData(response);
      console.log('AddService: Background refresh completed for:', response?.service?.service_number);
    } catch (error) {
      console.error('AddService: Background refresh failed:', error);
      // Don't show error to user for background refreshes
    }
  }, [selectedService?.id, selectedService?.service_number]);

  // Set up auto-refresh interval when a service is selected
  useEffect(() => {
    // Clear any existing interval
    if (autoRefreshInterval) {
      clearInterval(autoRefreshInterval);
      setAutoRefreshInterval(null);
    }

    // Set up new interval if a service is selected
    if (selectedService?.id) {
      console.log('AddService: Setting up auto-refresh for service:', selectedService.service_number);
      const interval = setInterval(() => {
        refreshServiceDetailsInBackground();
      }, 30000); // 30 seconds

      setAutoRefreshInterval(interval);
    }

    // Cleanup function
    return () => {
      if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
        setAutoRefreshInterval(null);
      }
    };
    // autoRefreshInterval intentionally omitted to avoid resetting the timer on each set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService?.id, selectedService?.service_number, refreshServiceDetailsInBackground]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
      }
    };
  }, [autoRefreshInterval]);

  // Get contact number - no need to memoize this simple value
  const contactNumber = caseData?.customer?.mobile_number || '';

  const customerDetails = useMemo(() => {
    if (!caseData) return {};
    return {
      customer: {
        id: caseData.customer?.id,
        name: caseData.customer?.name,
        mobile_number: caseData.customer?.mobile_number,
        email: caseData.customer?.email,
      },
      policy: {
        insurance_provider: caseData.client?.insurance_company_name,
        policy_type: caseData.policy?.policy_type,
      },
      vehicle: {
        make: caseData.vehicle?.make,
        model: caseData.vehicle?.model,
        registration_number: caseData.vehicle?.registration_number,
        vin: caseData.vehicle?.vin || caseData.vehicle?.vin_number,
        connected: caseData.vehicle?.connected,
      },
    };
  }, [caseData]);

  const caseStatusLabel = useMemo(() => {
    const raw = caseData?.case?.status || 'open';
    return String(raw).replace(/_/g, ' ');
  }, [caseData?.case?.status]);

  const caseStatusBadgeClass = useMemo(() => {
    const s = (caseData?.case?.status || '').toLowerCase();
    if (s === 'closed' || s === 'resolved') return 'bg-emerald-50 text-emerald-800 ring-emerald-600/15';
    if (s === 'cancelled') return 'bg-red-50 text-red-800 ring-red-600/15';
    if (s === 'in_progress') return 'bg-blue-50 text-blue-800 ring-blue-600/15';
    return 'bg-slate-100 text-slate-800 ring-slate-600/10';
  }, [caseData?.case?.status]);

  const trackVendorEnabled = useMemo(() => {
    const id = selectedService?.id;
    const st = detailedServiceData?.service?.status;
    return Boolean(id && st && serviceStatusAllowsVendorTracking(st));
  }, [selectedService?.id, detailedServiceData?.service?.status]);

  const trackVendorTitle = trackVendorEnabled
    ? 'Open live vendor map'
    : selectedService?.id
      ? 'Tracking is available after the vendor has accepted and started dispatch'
      : 'Select a service to track the vendor';

  const handleActivityCounts = useCallback((counts) => {
    setActivityCounts((prev) => {
      if (prev.notes === counts.notes && prev.files === counts.files) return prev;
      return counts;
    });
  }, []);

  const handlePhotosCountKpi = useCallback((n) => {
    setPhotoCountKpi(typeof n === 'number' ? n : 0);
  }, []);

  useEffect(() => {
    setTrackVendorModalOpen(false);
  }, [selectedService?.id]);

  // Open tracking modal when arriving from Services "Track Vendor" (?track=1)
  useEffect(() => {
    if (searchParams.get('track') !== '1') return;
    if (!trackVendorEnabled || !selectedService?.id || loadingDetailedService) return;

    setTrackVendorModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('track');
    setSearchParams(next, { replace: true });
  }, [
    searchParams,
    setSearchParams,
    trackVendorEnabled,
    selectedService?.id,
    loadingDetailedService,
  ]);

  const openVendorTrackingModal = useCallback(() => {
    setTrackVendorModalOpen(true);
  }, []);

  const closeVendorTrackingModal = useCallback(() => {
    setTrackVendorModalOpen(false);
  }, []);

  useEffect(() => {
    if (!selectedService?.id) {
      setPhotoCountKpi(0);
    }
  }, [selectedService?.id]);

  const kpi = useMemo(
    () => ({
      services: caseData?.services?.length ?? 0,
      notes: activityCounts.notes,
      files: activityCounts.files,
      photos: photoCountKpi,
    }),
    [caseData?.services?.length, activityCounts.notes, activityCounts.files, photoCountKpi]
  );

  if (loadingCase) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl font-semibold">Loading case details...</div>
      </div>
    );
  }

  if (errorCase) {
    return (
      <div className="max-w-4xl mx-auto mt-10 p-8 bg-white rounded-lg shadow">
        <h2 className="text-xl font-bold text-red-600 mb-4">Error</h2>
        <p className="mb-4">{errorCase}</p>
        <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={() => navigate('/services')}>Back to Services</button>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="max-w-4xl mx-auto mt-10 p-8 bg-white rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4">No case data found.</h2>
        <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={() => navigate('/services')}>Back to Services</button>
      </div>
    );
  }

  return (
    <div className="case-workspace-premium min-w-0">
      <CustomerDetailsModal
        visible={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        details={customerDetails}
      />
      {trackVendorModalOpen && selectedService?.id ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="flex h-[95vh] max-h-[95vh] w-[95vw] max-w-7xl flex-col overflow-hidden rounded-lg bg-white">
            <div className="flex shrink-0 items-center justify-between border-b p-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Vendor Tracking - {selectedService.service_number}
              </h3>
              <button
                type="button"
                onClick={closeVendorTrackingModal}
                className="text-2xl font-bold text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden p-0">
              <VendorLiveTracking
                serviceId={selectedService.id}
                isIframe={true}
                polyline={
                  detailedServiceData?.service?.status === 'service_resolved' ||
                  detailedServiceData?.service?.status === 'service_complete'
                    ? detailedServiceData?.service?.meta_data?.polyline || null
                    : null
                }
              />
            </div>
          </div>
        </div>
      ) : null}
      <CaseWorkspaceShell
        caseNumber={caseData.case?.case_number || String(case_id)}
        caseStatusLabel={caseStatusLabel}
        caseStatusBadgeClass={caseStatusBadgeClass}
        onBreadcrumbServices={() => navigate('/services')}
        trackVendorEnabled={trackVendorEnabled}
        trackVendorTitle={trackVendorTitle}
        onTrackVendor={openVendorTrackingModal}
        serviceId={selectedService?.id}
        detailedServiceData={detailedServiceData}
        loadingDetailedService={loadingDetailedService}
        paymentRefreshKey={paymentRefreshKey}
        aboveColumns={
          <CaseWorkspaceHero
            caseData={caseData}
            customerName={caseData.customer?.name || 'Customer'}
            onOpenCustomer={() => setCustomerModalOpen(true)}
            kpi={kpi}
          />
        }
      >
        <CaseDetailsSegment caseObj={caseData} onOpenCustomer={() => setCustomerModalOpen(true)} />
        <CaseNotesSegment
          caseId={caseData.case?.id}
          onActivityCountsChange={handleActivityCounts}
          refreshKey={notesRefreshKey}
        />
        <CaseServicesSegment
          services={caseData.services || []}
          onSelectService={handleServiceSelection}
          selectedService={selectedService}
          detailedServiceData={detailedServiceData}
          contactNumber={contactNumber}
          caseId={caseData.case?.id}
          schemeId={caseData.client_scheme?.id}
          onServiceAdded={handleServiceAdded}
          onServiceCreated={handleServiceCreated}
          loadingServices={loadingCase}
          caseObj={caseData}
          onServiceTypesLoaded={handleServiceTypesLoaded}
        />
        <ServiceDetailsSegment
          service={selectedService}
          detailedServiceData={detailedServiceData}
          caseObj={caseData}
          onServiceUpdated={handleServiceUpdated}
          onPaymentUpdated={handlePaymentActivity}
          serviceTypes={serviceTypes}
          loadingDetailedService={loadingDetailedService}
          onPhotosCountChange={handlePhotosCountKpi}
          onTrackVendor={openVendorTrackingModal}
        />
      </CaseWorkspaceShell>
    </div>
  );
};

export default AddService; 